import React, { useEffect, useRef, useState } from 'react';
import { fetchFlowSessionCalendarHistory, flowReadError, SessionCalendarPage } from '../../lib/mackFlowRead';
import { panelNote } from './primitives';
import { C } from './tokens';

export function FlowCalendarHistory({ market, consumer, token, generation }: {
  market: string; consumer: string; token?: string | null; generation: string;
}) {
  const [page, setPage] = useState<SessionCalendarPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const active = useRef<AbortController | null>(null);
  useEffect(() => {
    active.current?.abort(); active.current = null; setPage(null); setError(null); setLoading(false);
    return () => { active.current?.abort(); active.current = null; };
  }, [consumer, token, generation]);
  const read = async () => {
    active.current?.abort(); const controller = new AbortController(); active.current = controller;
    setLoading(true); setError(null);
    try {
      const result = await fetchFlowSessionCalendarHistory(consumer, token, controller.signal);
      if (active.current === controller && !controller.signal.aborted) setPage(result);
    } catch (failure) {
      if (active.current === controller && !controller.signal.aborted) setError(flowReadError(failure).message);
    } finally { if (active.current === controller) setLoading(false); }
  };
  return <section aria-label={`${market} calendar requirements and history`} style={{ marginTop: 12 }}>
    <button type="button" disabled={loading} onClick={() => void read()}
      style={{ background: C.chipOffBg, color: C.amberBright, border: `1px solid ${C.chipOffBd}`, borderRadius: 6, padding: '7px 11px' }}>
      {loading ? 'Reading calendar history…' : `Read calendar requirements and history for ${market}`}
    </button>
    {error && <p role="alert" style={panelNote}>{error}</p>}
    {page && <>
      <p style={panelNote}>Original source acceptance and economic acknowledgement remain unproved. The number of governed observations is unknown.</p>
      <p style={panelNote}>{page.calendar_dependencies.retention_binding_state === 'INSTALLED_BOE_DOCUMENT_RETENTION_BINDING'
        ? 'The exact BoE document retention binding is installed. Retained document metadata does not prove applicable publication policy.'
        : 'The selected contracts have no document retention and consumer acknowledgement binding.'}</p>
      {page.rows.map((row, index) => <div key={String(row.claim_id || index)} style={panelNote}>
        <strong>Retained document dependency</strong> · {String(row.request_id)} · {String(row.edition_label || 'Edition unknown')}
        <div>Original SHA-256: {String(row.original_sha256)}</div>
        <div>Retrieved: {String(row.retrieved_at || 'Unknown')} · Publication: {String(row.publication_at || 'Unknown')}</div>
        <div>Technical retention verified; source applicability and economic ACK remain held.</div>
      </div>)}
      {page.calendar_dependencies.required_originals.map(original => <div key={original.request_id} style={panelNote}>
        <strong>{original.publisher}</strong> · {original.dataset}
        {original.canonical_source_urls.map(url => <div key={url}><a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.amber }}>Original source</a></div>)}
      </div>)}
      <p style={panelNote}>Session availability, participant presence and liquidity effects remain unavailable.</p>
    </>}
  </section>;
}
